import { Button } from "@rosc/ui/components/button";
import { Eye } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppConfig } from "@/common/appConfig";
import { V3Button } from "@/components/ui/v3/button";
import { stopImpersonation } from "@/lib/impersonation";
import { useNativeAuthStore } from "@/store/native-auth.store";

export function ImpersonationBanner() {
  const { t } = useTranslation();
  const session = useNativeAuthStore((state) => state.session);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState(false);
  if (!session?.impersonatedBy) return null;
  const stop = async (): Promise<void> => {
    setPending(true);
    setError(false);
    try {
      await stopImpersonation();
    } catch {
      setError(true);
      setPending(false);
    }
  };
  const label = t("impersonation.viewingAs", { email: session.user.email });
  const action = pending
    ? t("impersonation.stopping")
    : t("impersonation.stop");
  const failed = error && (
    <p
      className={
        AppConfig.enableAppRedesign
          ? "w-full text-[13px] font-medium text-v3-rec-text"
          : undefined
      }
      role="alert"
    >
      {t("impersonation.stopFailed")}
    </p>
  );

  // v3.2 (VITE_APP_REDESIGN_ENABLED): a warn-tinted bar across the top of
  // the page panel.
  if (AppConfig.enableAppRedesign)
    return (
      <div
        role="status"
        className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 border-b border-[color-mix(in_srgb,var(--v3-warn)_25%,transparent)] bg-v3-warn-tint px-4 py-2 font-v3 text-[13.5px] text-v3-text"
        data-testid="impersonation-banner"
      >
        <Eye aria-hidden className="size-4 shrink-0 text-v3-warn-text" />
        <span className="min-w-0 flex-1 font-medium">{label}</span>
        <V3Button disabled={pending} variant="secondary" onClick={stop}>
          {action}
        </V3Button>
        {failed}
      </div>
    );

  return (
    <div
      role="status"
      className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b bg-amber-100 px-4 py-2 text-amber-950"
    >
      <span>{label}</span>
      <Button variant="outline" size="sm" disabled={pending} onClick={stop}>
        {action}
      </Button>
      {failed}
    </div>
  );
}
